// Section 22 event prompt and Section 14 arrival screen (stream E2): what the UI shows for an open
// event prompt: its text, each choice's label with its one-line consequence preview (where the
// engine can compute one), and for a group at the gate the candidates as the player can see them
// this season: names, ages, visible health, claimed skills (true 80% of the time) and whether
// they look sick (a sick arrival shows as sick 70% of the time). Everything is a narration
// (template id and params); the prose lives in ui/text/en.ts.
import { config, type Config } from '../config';
import { registryFor } from '../content';
import type {
  EventId,
  EventPrompt,
  GameState,
  GroupId,
  PersonId,
  Prompt,
  PromptId,
  Sex,
  SkillId,
} from '../core/types';
import { readContext } from './context';
import { eventTemplate, type Narration, type NarrationTemplateId } from './narrate';

export interface EventChoiceView {
  id: string;
  label: Narration;
  /** One line per consequence the engine can compute now (may be empty). */
  previews: Narration[];
  /** This choice takes a list of people (Section 14 "admit some"). */
  picksPeople: boolean;
}

export interface ArrivalCandidateView {
  personId: PersonId;
  name: string;
  sex: Sex;
  ageYears: number;
  child: boolean;
  /** Visible health, 0..100 (rounded). */
  health: number;
  /** What they claim at the gate (or, for guests decided on again, what they have shown). */
  claimed: { skill: SkillId; level: number }[];
  looksSick: boolean;
}

export interface EventPromptView {
  promptId: PromptId;
  eventId: EventId;
  turn: number;
  text: Narration;
  choices: EventChoiceView[];
  defaultChoiceId: string;
  groupId: GroupId | null;
  /** Guests being decided on again after their stay (Section 14). */
  followUp: boolean;
  candidates: ArrivalCandidateView[];
}

function readable(params: EventPrompt['params']): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) if (k !== 'seed') out[k] = v;
  return out;
}

function claims(skills: Partial<Record<SkillId, number>>): { skill: SkillId; level: number }[] {
  return (Object.entries(skills) as [SkillId, number][])
    .filter(([, level]) => level > 0)
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    .slice(0, 3)
    .map(([skill, level]) => ({ skill, level: Math.round(level * 10) / 10 }));
}

function candidatesOf(
  state: GameState,
  prompt: EventPrompt,
  cfg: Config,
  followUp: boolean,
): ArrivalCandidateView[] {
  const per = cfg.people.seasonsPerYear;
  const adultAt = cfg.people.adultAtSeasons;
  if (followUp) {
    const out: ArrivalCandidateView[] = [];
    for (const id of prompt.subjectIds) {
      const p = state.people[id];
      if (p === undefined) continue;
      out.push({
        personId: p.id,
        name: p.name,
        sex: p.sex,
        ageYears: Math.floor(p.ageSeasons / per),
        child: p.ageSeasons < adultAt,
        health: Math.round(p.health),
        claimed: claims(p.skills),
        looksSick: p.status.sick !== null,
      });
    }
    return out;
  }
  const group = prompt.groupId === null ? undefined : state.arrivalGroups[prompt.groupId];
  if (group === undefined) return [];
  return group.candidates.map((p) => ({
    personId: p.id,
    name: p.name,
    sex: p.sex,
    ageYears: Math.floor(p.ageSeasons / per),
    child: p.ageSeasons < adultAt,
    health: Math.round(p.health),
    claimed: claims(group.claimedSkills[p.id] ?? {}),
    looksSick: group.visiblySick.includes(p.id),
  }));
}

function textOf(prompt: EventPrompt, followUp: boolean): Narration {
  const params = readable(prompt.params);
  if (followUp) return { template: 'event.gate.followup', params };
  if (prompt.groupId !== null && prompt.eventId === 'refugees')
    return { template: eventTemplate('refugees', { variant: String(params.reason) }), params };
  return { template: eventTemplate(prompt.eventId, prompt.params), params };
}

/** The view of one open event prompt. */
export function eventPromptView(
  state: GameState,
  prompt: EventPrompt,
  cfg: Config = config,
): EventPromptView {
  const ctx = readContext(state, cfg);
  const def = registryFor(cfg).events[prompt.eventId];
  const followUp = prompt.params.followUp === 1;
  const params = readable(prompt.params);
  const choices = prompt.choices.map((c): EventChoiceView => {
    const hook = def.choices.find((d) => d.id === c.id)?.preview ?? null;
    const previews = (hook === null ? [] : hook(ctx, prompt)).map((line) => ({
      template: `preview.${line.key}` as NarrationTemplateId,
      params: line.params,
    }));
    return {
      id: c.id,
      label: { template: `choice.${prompt.eventId}.${c.id}` as NarrationTemplateId, params },
      previews,
      picksPeople: c.id === 'admit-some',
    };
  });
  return {
    promptId: prompt.id,
    eventId: prompt.eventId,
    turn: prompt.turn,
    text: textOf(prompt, followUp),
    choices,
    defaultChoiceId: prompt.defaultChoiceId,
    groupId: prompt.groupId,
    followUp,
    candidates: prompt.groupId === null ? [] : candidatesOf(state, prompt, cfg, followUp),
  };
}

/** Every open event prompt (Section 19: presented in the Report, answered in phase 2). */
export function eventPromptViews(state: GameState, cfg: Config = config): EventPromptView[] {
  return state.openPrompts
    .filter((p: Prompt): p is EventPrompt => p.kind === 'event')
    .map((p) => eventPromptView(state, p, cfg));
}
